import React, { useEffect, useState } from "react";
import { DataGrid } from "@mui/x-data-grid";
import RemoveCircleOutlineIcon from '@mui/icons-material/RemoveCircleOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import { 
  Box, 
  Typography, 
  LinearProgress,
  TextField, 
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Chip,
  Avatar,
  Paper,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import Grid from '@mui/material/Grid2';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import DownloadIcon from '@mui/icons-material/Download';
import dayjs from 'dayjs';
import axiosInstance from "./axiosInstance";

import Tooltip from '@mui/material/Tooltip';
import { formatBytes, truncateFileName, getMimeType, getFileIcon } from "../utils/fileUtils";

import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';

// File icons
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import DescriptionIcon from "@mui/icons-material/Description";
import TableChartIcon from "@mui/icons-material/TableChart";
import ArticleIcon from "@mui/icons-material/Article";
import UploadFileIcon from "@mui/icons-material/UploadFile";


import { useSnackbar } from "../providers/SnackbarProvider";
import { useBreadcrumbs } from './BreadcrumbBar';
import useIsManager from '../hooks/useIsManager';
import EmptyState from './ui/EmptyState';

// Leerzustand für ein leeres Such-/Filterergebnis (D4)
const NoResultsOverlay = () => (
  <EmptyState title="Nichts gefunden." hint="Such- oder Filterkriterien anpassen." />
);


const ContractList = () => {
  const { showSnackbar } = useSnackbar();
  const isManager = useIsManager();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [projects, setProjects] = useState([]);
  const [consultants, setConsultants] = useState([]);
  const [attachments, setAttachments] = useState([]);
  const [currentTab, setCurrentTab] = useState(0);
  const [uploading, setUploading] = useState(false);
  
  const [newContract, setNewContract] = useState({
    project: '',
    consultant_assignments: [{ consultant: '', rate: 0, hours: 0 }],
    start_date: dayjs().format('YYYY-MM-DD'),
    end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
    status: 'Draft',
    notes: '',
    contract_file: null
  });

  // Breadcrumbs
  useBreadcrumbs(
       [
          { label: 'Verträge' },
        ]
  );


  useEffect(() => {
    fetchContracts();
    fetchProjects();
    fetchConsultants();
  }, []);

  const fetchAttachments = async (contractId) => {
    try {
      const response = await axiosInstance.get('/attachments/', {
        params: {
          object_type: 'contract',
          object_id: contractId,
        },
      });
      console.log("Attachments:");
      console.log(response);
      setAttachments(response.data);
    } catch (error) {
      console.error('Error fetching attachments', error);
    }
  };

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue);
  };

  const handleDownloadAttachment = async (file) => {
    const response = await axiosInstance.get(`/attachments/${file.id}/download/`, {
      responseType: 'blob',
    });
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  const handleDeleteAttachment = async (id) => {
    console.log(id);
    if (!window.confirm('Möchten Sie die Datei wirklich löschen?')) return;
    try {
      await axiosInstance.delete(`/attachments/${id}/`);
      setAttachments((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Fehler beim Löschen:', err);
    }
  };

  const handleUploadAttachment = async (event) => {
    const file = event.target.files[0];
    if (!file || !selectedContract?.id) return;

    const formData = new FormData();
    formData.append('file', file);
    formData.append('object_type', 'contract');
    formData.append('object_id', selectedContract.id);

    setUploading(true);
    try {
      await axiosInstance.post('/attachments/', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      showSnackbar('Datei erfolgreich hochgeladen', 'success');
      fetchAttachments(selectedContract.id);
    } catch (error) {
      console.error('Error uploading attachment:', error);
      showSnackbar('Fehler beim Hochladen der Datei', 'error');
    } finally {
      setUploading(false);
      // Reset so the same file can be re-uploaded if needed
      event.target.value = '';
    }
  };

  const fileIconMap = {
    image: <ImageIcon fontSize="small" />,
    pdf: <PictureAsPdfIcon fontSize="small" />,
    text: <DescriptionIcon fontSize="small" />,
    excel: <TableChartIcon fontSize="small" />,
    word: <ArticleIcon fontSize="small" />,
    file: <InsertDriveFileIcon fontSize="small" />,
  };

  const fetchContracts = async () => {
    try {
      const response = await axiosInstance.get("/contracts/");
      setContracts(response.data);
    } catch (error) {
      console.error("Error fetching contracts:", error);
      showSnackbar('Fehler beim Laden der Verträge', "error");
    } finally {
      setLoading(false);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await axiosInstance.get('/projects/');
      setProjects(response.data);
    } catch (error) {
      console.error('Error fetching projects:', error);
    }
  };

  const fetchConsultants = async () => {
    try {
      const response = await axiosInstance.get('/users/');
      setConsultants(response.data);
    } catch (error) {
      console.error('Error fetching consultants:', error);
    }
  };

  const formatDate = (date) => {
    return dayjs(date).format('MMM D, YYYY');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(value);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'Active': return 'success';
      case 'Completed': return 'info';
      case 'Cancelled': return 'error';
      default: return 'default';
    }
  };

  const getConsultantName = (consultantId) => {
    const consultant = consultants.find(c => c.id === consultantId);
    if (consultant?.first_name && consultant?.last_name) {
      return `${consultant.first_name} ${consultant.last_name}`;
    }
    return consultant?.email || 'Unbekannt';
  };

  const addConsultantAssignment = (isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: [
          ...prev.consultant_assignments,
          { consultant: '', rate: 0, hours: 0 }
        ]
      })); 
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: [
          ...prev.consultant_assignments,
          { consultant: '', rate: 0, hours: 0 }
        ]
      }));
    }
  };

  const removeConsultantAssignment = (index, isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.filter((_, i) => i !== index)
      }));
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.filter((_, i) => i !== index)
      }));
    }
  };

  const updateConsultantAssignment = (index, field, value, isEdit = false) => {
    if (isEdit) {
      setSelectedContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.map((assignment, i) =>
          i === index ? { ...assignment, [field]: value } : assignment
        )
      }));
    } else {
      setNewContract(prev => ({
        ...prev,
        consultant_assignments: prev.consultant_assignments.map((assignment, i) =>
          i === index ? { ...assignment, [field]: value } : assignment
        )
      }));
    }
  };

  const getInitials = (name) => {
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleCreateContract = async () => {
    if (!newContract.project) {
      showSnackbar("Projekt ist erforderlich", "error");
      return;
    }

    const validAssignments = newContract.consultant_assignments.filter(
      a => a.consultant && a.hours > 0 && a.rate > 0
    );

    if (validAssignments.length === 0) {
      showSnackbar("Mindestens ein Consultant mit Stunden und Rate ist erforderlich", "error");
      return;
    }

    if (dayjs(newContract.end_date).isBefore(dayjs(newContract.start_date))) {
      showSnackbar('End Date muss nach Start Date liegen', "error");
      return;
    }

    try {
      const response = await axiosInstance.post('/contracts/', {
        title: newContract.title,
        project: newContract.project,
        consultant_assignments: validAssignments,
        start_date: newContract.start_date,
        end_date: newContract.end_date,
        status: newContract.status,
        notes: newContract.notes || ''
      });
      
      setContracts([response.data, ...contracts]);

      setCreateDialogOpen(false);
      
      setNewContract({
        title: '',
        project: '',
        consultant_assignments: [{ consultant: '', rate: 0, hours: 0 }],
        start_date: dayjs().format('YYYY-MM-DD'),
        end_date: dayjs().add(1, 'year').format('YYYY-MM-DD'),
        status: 'Draft',
        notes: '',
        contract_file: null
      });

      showSnackbar('Vertrag erfolgreich erstellt', "success");
    } catch (error) {
      console.error('Error creating contract:', error);
      console.error('Error response:', error.response?.data);
      showSnackbar(`Fehler beim Erstellen des Vertrags: ${JSON.stringify(error.response?.data) || error.message}`, "error");
    }
  };

  const handleInputChange = (event) => {
    const { name, value, files } = event.target;
    setNewContract(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleEditInputChange = (event) => {
    const { name, value, files } = event.target;
    setSelectedContract(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };

  const handleOpenEditDialog = (contract) => {
    
    fetchAttachments(contract.id);
    
    const preparedContract = {
      ...contract,
      consultant_assignments: contract.consultant_assignments || 
        (contract.consultant ? [{ 
          consultant: contract.consultant, 
          rate: contract.rate || 0, 
          hours: contract.hours || 0 
        }] : [{ consultant: '', rate: 0, hours: 0 }])
    };
    
    console.log('Prepared contract:', preparedContract);
    
    setSelectedContract(preparedContract);
    setEditDialogOpen(true);
  };

  const EditableNumberCell = ({ value, onUpdate, min = 0, step = 0.01, width = 120 }) => {
    const [localValue, setLocalValue] = React.useState(value || '');
    const inputRef = React.useRef(null);

    React.useEffect(() => {
      setLocalValue(value || '');
    }, [value]);

    const handleChange = (e) => {
      setLocalValue(e.target.value);
    };

    const handleBlur = () => {
      const numValue = parseFloat(localValue) || 0;
      onUpdate(numValue);
    };

    const handleKeyPress = (e) => {
      if (e.key === 'Enter') {
        inputRef.current?.blur();
      }
    };

    return (
      <TextField
        ref={inputRef}
        type="number"
        value={localValue}
        onChange={handleChange}
        onBlur={handleBlur}
        onKeyPress={handleKeyPress}
        size="small"
        inputProps={{ min, step }}
        sx={{ width }}
      />
    );
  };

  const ConsultantAssignmentSection = ({ assignments, isEdit = false }) => (
    <Grid size={{ xs: 12}}>
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Beraterzuweisung</Typography>
          <Button
            startIcon={<PersonAddIcon />}
            onClick={() => addConsultantAssignment(isEdit)}
            size="small"
          >
            Berater hinzufügen
          </Button>
        </Box>
        <Divider sx={{ mb: 2 }} />
        
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Berater</TableCell>
                <TableCell align="right">Stundenrate (€)</TableCell>
                <TableCell align="right">Stunden</TableCell>
                <TableCell align="right">Gesamt</TableCell>
                <TableCell align="center">Aktionen</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {assignments.map((assignment, index) => (
                <TableRow key={`${assignment.consultant}-${index}`}>
                  <TableCell>
                    <FormControl fullWidth size="small" required>
                      <Select
                        value={assignment.consultant || ''}
                        onChange={(e) => updateConsultantAssignment(index, 'consultant', e.target.value, isEdit)}
                        displayEmpty
                      >
                        <MenuItem value="" disabled>Berater auswählen</MenuItem>
                        {consultants.map(consultant => (
                          <MenuItem key={consultant.id} value={consultant.id}>
                            {consultant.first_name && consultant.last_name
                              ? `${consultant.first_name} ${consultant.last_name}`
                              : consultant.email}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="right">
                    <EditableNumberCell
                      value={assignment.rate}
                      onUpdate={(newValue) => updateConsultantAssignment(index, 'rate', newValue, isEdit)}
                      step={1}
                      width={120}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <EditableNumberCell
                      value={assignment.hours}
                      onUpdate={(newValue) => updateConsultantAssignment(index, 'hours', newValue, isEdit)}
                      step={0.5}
                      width={100}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" fontWeight="bold">
                      {formatCurrency((assignment.rate || 0) * (assignment.hours || 0))}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      onClick={() => removeConsultantAssignment(index, isEdit)}
                      color="error"
                      size="small"
                      disabled={assignments.length === 1}
                    >
                      <RemoveCircleOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right">
                  <Typography variant="subtitle1" fontWeight="bold">Gesamtbetrag:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {formatCurrency(assignments.reduce((sum, a) => sum + ((a.rate || 0) * (a.hours || 0)), 0))}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
              <TableRow>
                <TableCell colSpan={3} align="right">
                  <Typography variant="subtitle1" fontWeight="bold">Gesamtstunden:</Typography>
                </TableCell>
                <TableCell align="right">
                  <Typography variant="subtitle1" fontWeight="bold" color="primary">
                    {assignments.reduce((sum, a) => sum + Number(a.hours || 0), 0).toFixed(2)}
                  </Typography>
                </TableCell>
                <TableCell />
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Grid>
  );

  const handleSaveContract = async () => {
    if (!selectedContract.project) {
      showSnackbar("Projekt ist erforderlich", "error");
      return;
    }

    const validAssignments = selectedContract.consultant_assignments
      .filter(a => a.consultant && a.hours > 0 && a.rate > 0)
      .map(a => ({
        ...(a.id && { id: a.id }),
        consultant: a.consultant,
        rate: a.rate,
        hours: a.hours
      }));

    if (validAssignments.length === 0) {
      showSnackbar("Mindestens ein Consultant mit Stunden und Rate ist erforderlich", "error");
      return;
    }

    if (dayjs(selectedContract.end_date).isBefore(dayjs(selectedContract.start_date))) {
      showSnackbar('End Date muss nach Start Date liegen', "error");
      return;
    }

    try {
      await axiosInstance.put(
        `/contracts/${selectedContract.id}/`,
        {
          title: selectedContract.title,
          project: selectedContract.project,
          consultant_assignments: validAssignments,
          start_date: selectedContract.start_date,
          end_date: selectedContract.end_date,
          status: selectedContract.status,
          notes: selectedContract.notes || ''
        }
      );
      
      await fetchContracts();
      
      setEditDialogOpen(false);
      showSnackbar('Vertrag erfolgreich aktualisiert', "success");
    } catch (error) {
      console.error('Error updating contract:', error);
      console.error('Error response:', error.response?.data);
      showSnackbar(`Fehler beim Aktualisieren des Vertrags: ${JSON.stringify(error.response?.data) || error.message}`, "error");
    }
  };

  const handleDeleteContract = async (id) => {
    if (!window.confirm('Möchten Sie diesen Vertrag wirklich löschen?')) {
      return;
    }

    try {
      await axiosInstance.delete(`/contracts/${id}/`);
      setContracts(contracts.filter(c => c.id !== id));
      showSnackbar('Vertrag erfolgreich gelöscht', "success");
    } catch (error) {
      console.error('Error deleting contract:', error);
      showSnackbar('Fehler beim Löschen des Vertrags', "error");
    }
  };

  const columns = [
    { field: "id", headerName: "ID", width: 50 },
    {
      field: "actions",
      headerName: "Aktionen",
      width: 90,
      renderCell: (params) => (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <IconButton
            variant="action-edit"
            onClick={() => handleOpenEditDialog(params.row)}
          >
            <EditIcon />
          </IconButton>
          {/* Vertrag löschen ist serverseitig Managern vorbehalten (Security S3) */}
          {isManager && (
            <IconButton
              variant="action-delete"
              onClick={() => handleDeleteContract(params.row.id)}
            >
              <DeleteIcon />
            </IconButton>
          )}
        </Box>
      ),
    },
    { 
      field: "project_name", 
      headerName: "Projekt", 
      flex: 1.5,
      minWidth: 150,
    },
    { 
      field: "title", 
      headerName: "Titel", 
      flex: 1.5,
      minWidth: 150,
    },
    {
      field: "consultants",
      headerName: "Berater",
      flex: 2,
      minWidth: 200,
      renderCell: (params) => {
        const assignments = params.row.consultant_assignments || [];
        
        return (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              height: "100%",
              gap: 0.5,
              flexWrap: "wrap",
            }}
          >
            {assignments.slice(0, 3).map((assignment, idx) => {
              const name = getConsultantName(assignment.consultant);
              return (
                <Tooltip key={idx} title={`${name} - ${assignment.hours}h @ ${formatCurrency(assignment.rate)}/h`} arrow>
                  <Avatar sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: 'primary.main' }}>
                    {getInitials(name)}
                  </Avatar>
                </Tooltip>
              );
            })}
            {assignments.length > 3 && (
              <Chip 
                label={`+${assignments.length - 3}`} 
                size="small" 
                sx={{ height: 24, fontSize: '0.75rem' }}
              />
            )}
          </Box>
        );
      },
    },
    {
      field: "start_date",
      headerName: "Startdatum",
      width: 130,
      renderCell: (params) => (
          formatDate(params.value)
      ),
    },
    {
      field: "end_date",
      headerName: "Enddatum",
      width: 130,
      renderCell: (params) => (
          formatDate(params.value)
      ),
    },
    { 
      field: "status", 
      headerName: "Status", 
      width: 110,
      renderCell: (params) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <Chip
              label={params.value}
              color={getStatusColor(params.value)}
              size="small"
            />
          </div>
      ),
    },
    {
      field: "total_value",
      headerName: "Gesamtwert",
      width: 120,
      renderCell: (params) => (
    <Typography
      variant="body2"
      fontWeight="bold"
      color="primary"
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center"
      }}
    >
          {formatCurrency(params.value)}
        </Typography>
      ),
    },
    {
      field: "total_hours",
      headerName: "Gesamtstunden",
      width: 120,
      renderCell: (params) => (
    <Typography
      variant="body2"
      fontWeight="bold"
      color="primary"
      sx={{
        height: "100%",
        display: "flex",
        alignItems: "center"
      }}
    >
          {params.value}
        </Typography>
      ),
    },
  ];

  const renderContractsTab = () => {
    return ( 
          <DialogContent>
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid size={{ xs: 12 }}>
                <TextField
                  name="title"
                  label="Titel"
                  value={selectedContract?.title || '' }
                  onChange={handleEditInputChange}
                  fullWidth
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth required>
                  <InputLabel>Projekt</InputLabel>
                  <Select
                    name="project"
                    value={selectedContract?.project || ''}
                    onChange={handleEditInputChange}
                    label="Project"
                  >
                    {projects.map(project => (
                      <MenuItem key={project.id} value={project.id}>
                        {project.account_name}: {project.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    value={selectedContract?.status || 'Draft'}
                    onChange={handleEditInputChange}
                    label="Status"
                  >
                    <MenuItem value="Draft">Entwurf</MenuItem>
                    <MenuItem value="Active">Aktiv</MenuItem>
                    <MenuItem value="Completed">Abgeschlossen</MenuItem>
                    <MenuItem value="Cancelled">Abgebrochen</MenuItem>
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="start_date"
                  label="Startdatum"
                  type="date"
                  value={selectedContract?.start_date || ''}
                  onChange={handleEditInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  name="end_date"
                  label="Enddatum"
                  type="date"
                  value={selectedContract?.end_date || ''}
                  onChange={handleEditInputChange}
                  fullWidth
                  required
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>

              <Grid size={{ xs: 12}}>
              {selectedContract && (
                <ConsultantAssignmentSection 
                  assignments={selectedContract.consultant_assignments} 
                  isEdit={true}
                />
              )}
              </Grid>

              <Grid size={{ xs: 12}}>
                <TextField
                  name="notes"
                  label="Notizen"
                  value={selectedContract?.notes || ''}
                  onChange={handleEditInputChange}
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </DialogContent>
    );
  };

  const renderContractsAttachments = () => {
    return (
      <DialogContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography>
            Anzahl Dateien: {attachments.length}
          </Typography>
          <Button
            variant="contained"
            component="label"
            startIcon={<UploadFileIcon />}
            size="small"
            disabled={uploading}
          >
            {uploading ? 'Wird hochgeladen…' : 'Datei hochladen'}
            <input
              type="file"
              hidden
              //accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={handleUploadAttachment}
            />
          </Button>
        </Box>

        {attachments.map((file) => (
          <Box
            key={file.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              p: 1,
              borderBottom: "1px solid",
              borderColor: "divider",
            }}
          >
            {/* Linke Seite: Icon + Dateiinformationen */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>


              <Box>
                <Tooltip title={file.name}>
                  <Typography variant="body2" fontWeight="medium">
                    {truncateFileName(file.name, 64)}
                  </Typography>
                </Tooltip>
                {file.description && (
                  <Typography variant="body2" fontWeight="medium">
                    Beschreibung: {truncateFileName(file.description, 64)}
                  </Typography>
                )}
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Tooltip title={getMimeType(file.name)}>
                    {fileIconMap[getFileIcon(getMimeType(file.name))]}
                  </Tooltip>
                  <Typography variant="caption" color="text.secondary">
                    {getMimeType(file.name)} • {formatBytes(file.size)}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Rechte Seite: Aktionen */}
            <Box>
              <Tooltip title="Öffnen">
                  <IconButton
                    variant="action-edit"
                    size="small"
                    onClick={async () => {
                      const response = await axiosInstance.get(`/attachments/${file.id}/download/`, {
                        responseType: 'blob',
                      });
                      const mimeType = response.headers['content-type']?.split(';')[0] || 'application/octet-stream';
                      const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: mimeType }));
                      const a = document.createElement('a');
                      a.href = blobUrl;
                      a.target = '_blank';
                      a.rel = 'noopener';
                      a.download = file.name;
                      document.body.appendChild(a);
                      a.click();
                      a.remove();
                      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10_000);
                    }}
                  >
                  <DescriptionIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Herunterladen">
                <IconButton
                  variant="action-edit"
                  size="small"
                  onClick={() => handleDownloadAttachment(file)}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>

              <Tooltip title="Löschen">
                <IconButton
                  variant="action-delete"
                  size="small"
                  onClick={() => handleDeleteAttachment(file.id)}
                >
                  <DeleteIcon />
                </IconButton>
              </Tooltip>

            </Box>
          </Box>
        ))}
      </DialogContent>
    );
  };

  // MAIN RETURN
  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">
          Verträge
        </Typography>
      </Box>

      <Grid size={12}>
        <DataGrid
          rows={contracts}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loading}
          pageSize={20}
          rowsPerPageOptions={[10, 20, 50]}
          disableSelectionOnClick
          slots={{
            noRowsOverlay: () => (
              <EmptyState
                title="Noch keine Verträge."
                actionLabel="Neuen Vertrag erstellen"
                onAction={() => setCreateDialogOpen(true)}
              />
            ),
            noResultsOverlay: NoResultsOverlay,
          }}
          sx={{
            // Platz für den Leerzustand inkl. Aktion (Default: 2 Zeilenhöhen)
            '--DataGrid-overlayHeight': '180px',
            '& .MuiDataGrid-cell': {
              borderBottom: '1px solid',
              borderColor: 'divider',
              py:0
            },
          }}
        />
      </Grid>

      {/* Create Contract Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Neuen Vertrag erstellen</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid size={{ xs: 12 }}>
              <TextField
                name="title"
                label="Titel"
                value={newContract.title || '' }
                onChange={handleInputChange}
                fullWidth
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth required>
                <InputLabel>Projekt</InputLabel>
                <Select
                  name="project"
                  value={newContract.project}
                  onChange={handleInputChange}
                  label="Project"
                >
                  {projects.map(project => (
                    <MenuItem key={project.id} value={project.id}>
                      {project.account_name}: {project.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  name="status"
                  value={newContract.status}
                  onChange={handleInputChange}
                  label="Status"
                >
                  <MenuItem value="Draft">Entwurf</MenuItem>
                  <MenuItem value="Active">Aktiv</MenuItem>
                  <MenuItem value="Completed">Abgeschlossen</MenuItem>
                  <MenuItem value="Cancelled">Abgebrochen</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="start_date"
                label="Startdatum"
                type="date"
                value={newContract.start_date}
                onChange={handleInputChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <TextField
                name="end_date"
                label="Enddatum"
                type="date"
                value={newContract.end_date}
                onChange={handleInputChange}
                fullWidth
                required
                InputLabelProps={{ shrink: true }}
              />
            </Grid>

            <Grid size={{ xs: 12}}>
              <ConsultantAssignmentSection 
                assignments={newContract.consultant_assignments} 
                isEdit={false}
              />
            </Grid>

            <Grid size={{ xs: 12 }}>
              <TextField
                name="notes"
                label="Notizen"
                value={newContract.notes}
                onChange={handleInputChange}
                multiline
                rows={3}
                fullWidth
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleCreateContract}
            variant="contained"
            color="primary"
            disabled={!newContract.project}
          >
            Erstellen
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Contract Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Vertrag bearbeiten</DialogTitle>

        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab label="Vertragsdaten" />
            <Tab label={`Dateien (${attachments.length})`} />
          </Tabs>
        </Box>
        
        {currentTab === 0 && renderContractsTab()}
        {currentTab === 1 && renderContractsAttachments()}

        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleSaveContract}
            variant="contained"
            color="primary"
          >
            Speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Floating Action Button */}
      <Fab
        color="primary"
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24
        }}
        onClick={() => setCreateDialogOpen(true)}
      >
        <AddIcon />
      </Fab>
    </>
  );
};

export default ContractList;